import React from 'react';

import { Layout, Sidebar } from '../../../shared/ui';
import {
    useAnalyticsTimeseries,
    useTimeTravelCurves,
    useTreasurySnapshot,
    // NEW API HOOKS
    useRealYieldSnapshot,
    useRealYieldTimeseries,
    useRealYieldEquityCorrelation,
} from './api/treasuryApi';
import { AnalyticsChart } from './components/AnalyticsChart';
import { TreasuryCommentary } from './components/TreasuryCommentary';
import { TimeTravelChart } from './components/TimeTravelChart';
import { TreasuryGrid } from './components/TreasuryGrid';
import { TreasuryKpiCards } from './components/TreasuryKpiCards';
import { formatDate } from './utils/formatters';

// NEW IMPORTS: Bring in the two advanced visualization components
import { RatesSeriesChart } from './components/RatesSeriesChart';
import { HistoricalCurveExplorer } from './components/HistoricalCurveExplorer';

// NEW VISUAL COMPONENTS
import { RealYieldCommentaryCard } from './components/RealYieldCommentaryCard';
import { MacroCorrelationChart } from './components/MacroCorrelationChart';
import { RealYieldTimeseriesChart } from './components/RealYieldTimeseriesChart';

export const TreasuryCurveDashboard: React.FC = () => {
    const {
        data: snapshot,
        isLoading: isSnapLoading,
        error: snapError,
    } = useTreasurySnapshot();
    const { data: timeTravel, isLoading: isTtLoading } = useTimeTravelCurves();
    // Fetch 5 years of chronological analytics once; AnalyticsChart slices
    // the array locally for zero-latency 1Y/3Y/5Y zooming.
    const { data: timeseries, isLoading: isTsLoading } = useAnalyticsTimeseries(5);

    // --- NEW REAL YIELD HOOKS ---
    const { data: realYieldSnapshot, isLoading: isRySnapLoading } = useRealYieldSnapshot();
    const { data: realYieldTimeseries, isLoading: isRyTsLoading } = useRealYieldTimeseries(5);
    const { data: equityCorrelation, isLoading: isEqCorrLoading } = useRealYieldEquityCorrelation(5);

    // Combine loading states
    const isLoading =
        isSnapLoading ||
        isTtLoading ||
        isTsLoading ||
        isRySnapLoading ||
        isRyTsLoading ||
        isEqCorrLoading;

    // Global Loading State
    if (isLoading) {
        return (
            <Layout sidebar={<Sidebar />}>
                <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
                    <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                    <div className="text-sm font-semibold text-slate-500 uppercase tracking-widest animate-pulse">
                        Loading Macro Engine...
                    </div>
                </div>
            </Layout>
        );
    }

    // Error State
    if (snapError || !snapshot) {
        return (
            <Layout sidebar={<Sidebar />}>
                <div className="p-4 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg shadow-sm dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
                    <h3 className="font-bold text-lg">Data Connection Failed</h3>
                    <p className="text-sm mt-1">
                        Unable to reach the Treasury API. Please verify your authentication
                        token or backend health.
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout sidebar={<Sidebar />}>
            <div className="space-y-6 text-slate-900 dark:text-slate-100 pb-12">
                {/* Header */}
                <header className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight">
                            Market Analysis Based on Treasury Yield Curve Analytics
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                            Real-time spreads, interest rate curve regimes, and macro
                            volatility signals.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Market Close
                        </div>
                        <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                            {formatDate(snapshot.date)}
                        </div>
                    </div>
                </header>

                {/* KPI WIDGETS ROW */}
                <TreasuryKpiCards snapshot={snapshot} />

                {/* ACTIONABLE STRATEGY PLAYBOOK ROW */}
                <TreasuryCommentary commentary={snapshot?.commentary} />

                {/* ========================================== */}
                {/* NEW SECTION: MACRO REAL YIELD & EQUITY     */}
                {/* ========================================== */}
                <div className="mt-12 mb-6">
                    <h2 className="text-2xl font-extrabold tracking-tight border-b border-slate-200 dark:border-slate-800 pb-2">
                        Macroeconomic Fundamentals Based on Real Yields Analysis
                    </h2>
                    <p className="text-sm text-slate-500 mt-2">
                        Analyzing the true cost of capital, inflation expectations, and cross-asset correlation. Discover
                        tactical positioning for your investment portfolio.
                    </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Left: 1/3 Width AI Playbook */}
                    <div className="xl:col-span-1 h-[500px]">
                        <RealYieldCommentaryCard commentary={realYieldSnapshot?.commentary} />
                    </div>
                    {/* Right: 2/3 Width Correlation Chart */}
                    <div className="xl:col-span-2 h-[500px]">
                        <MacroCorrelationChart data={equityCorrelation} />
                    </div>
                </div>

                {/* Full Width: Real Yield vs Forward Inflation Chart */}
                <div className="w-full h-[450px]">
                    <RealYieldTimeseriesChart data={realYieldTimeseries} />
                </div>

                <div className="mt-12 mb-6">
                    <h2 className="text-2xl font-extrabold tracking-tight border-b border-slate-200 dark:border-slate-800 pb-2">
                        Nominal Yield Curve Explorer
                    </h2>
                </div>

                {/* NEW FULL-WIDTH CHART: Interactive Multi-Tenor Series */}
                {/* Explicit height: the card's ResponsiveContainer uses height="100%",
                    which collapses to 0 (chart renders blank) unless an ancestor has a
                    definite height. Matches the AnalyticsChart wrapper pattern. */}
                <div className="w-full h-[550px]">
                    <RatesSeriesChart />
                </div>

                {/* CURVE SHAPE EXPLORATION: Side-by-Side Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {/* Left: Quick visual lookback over the last year */}
                    <div className="h-full">
                        <TimeTravelChart data={timeTravel} />
                    </div>
                    {/* Right: The new exact-date lookup tool */}
                    <div className="h-full">
                        <HistoricalCurveExplorer />
                    </div>
                </div>

                {/* EXISTING ANALYTICS CHART: Spreads & Volatility */}
                <div className="w-full h-[450px]">
                    <AnalyticsChart data={timeseries} />
                </div>

                {/* ADVANCED DATA GRID: Server-side paginated & filtered */}
                <div className="mt-8">
                    <TreasuryGrid />
                </div>
            </div>
        </Layout>
    );
};

export default TreasuryCurveDashboard;
